import { useEffect, useState, useCallback } from 'react';
import { resolveCanonicalSymbol } from '../utils/coinSymbols';

export function normalizeSymbols(input: readonly unknown[]): string[] {
  if (!Array.isArray(input)) return [];
  const out: string[] = [];
  for (const v of input) {
    const rawSymbol = (v && typeof v === 'object' && 'symbol' in v)
      ? (v as Record<string, unknown>).symbol
      : v;
    if (rawSymbol == null) continue;
    const symbol = String(rawSymbol).trim();
    if (!symbol) continue;
    // Keep unknown configured symbols usable while normalizing all known
    // symbols and CoinGecko-style slugs through the one shared mapping.
    out.push(resolveCanonicalSymbol(symbol) || symbol.toUpperCase());
  }
  return Array.from(new Set(out));
}

export type RuntimeConfig = {
  coins: { slug: string; symbol: string }[];
  engineCoins: Record<'dream' | 'horizon' | 'scalp', string[]>;
};

export const CANONICAL_SETTINGS = {
  symbols: ['BTC', 'ETH', 'LTC', 'SOL', 'SUI', 'XRP', 'ADA', 'AVAX', 'BNB', 'HBAR'],
  engines: {
    dream: ['AVAX', 'BNB', 'BTC', 'ADA', 'ETH', 'HBAR', 'LTC', 'XRP', 'SOL', 'SUI'],
    scalp: ['AVAX', 'BNB', 'BTC', 'ETH', 'LTC', 'XRP', 'SOL', 'SUI'],
    horizon: ['AVAX', 'BNB', 'BTC', 'ADA', 'ETH', 'HBAR', 'LTC', 'XRP', 'SOL', 'SUI'],
  },
} as const;

export const SYMBOL_ID_MAP: Record<string, string> = {
  BTC: 'bitcoin',
  ETH: 'ethereum',
  LTC: 'litecoin',
  SOL: 'solana',
  SUI: 'sui',
  XRP: 'ripple',
  ADA: 'cardano',
  AVAX: 'avalanche-2',
  BNB: 'binancecoin',
  HBAR: 'hedera-hashgraph',
};

export function deriveEngineCoins(cfg: unknown): Record<'dream' | 'horizon' | 'scalp', string[]> {
  const cfgRecord = cfg && typeof cfg === 'object' ? cfg as Record<string, unknown> : {};
  const rawEngines = cfgRecord.engines;
  const src = rawEngines && typeof rawEngines === 'object' ? rawEngines as Record<string, unknown> : {};
  const dream = normalizeSymbols(Array.isArray(src.dream) ? src.dream : CANONICAL_SETTINGS.engines.dream);
  const horizon = normalizeSymbols(Array.isArray(src.horizon) ? src.horizon : CANONICAL_SETTINGS.engines.horizon);
  const scalp = normalizeSymbols(Array.isArray(src.scalp) ? src.scalp : CANONICAL_SETTINGS.engines.scalp);
  return { dream, horizon, scalp };
}

const FALLBACK_CONFIG: RuntimeConfig = {
  coins: CANONICAL_SETTINGS.symbols.map((sym) => ({ slug: SYMBOL_ID_MAP[sym], symbol: sym })),
  engineCoins: deriveEngineCoins(CANONICAL_SETTINGS),
};

export function useRuntimeConfig() {
  const [config, setConfig] = useState<RuntimeConfig>(FALLBACK_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/coin_settings.json', { cache: 'no-store' });
      if (!res.ok) throw new Error('failed');
      const data = await res.json();
      const symbols = normalizeSymbols(Array.isArray(data?.symbols) ? data.symbols : CANONICAL_SETTINGS.symbols);
      const engineCoins = deriveEngineCoins(data);
      const coins = symbols.map((sym: string) => ({ slug: SYMBOL_ID_MAP[sym], symbol: sym }));
      setConfig({ coins, engineCoins });
    } catch (error: unknown) {
      setError(error instanceof Error ? error : new Error(String(error)));
      setConfig(FALLBACK_CONFIG);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { config, loading, error, reload: load } as const;
}
